'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type MutableRefObject, type OutputHTMLAttributes, useRef, useState } from 'react';
import { mergeProps, useFocusRing, useNumberFormatter, useSlider, useSliderThumb, VisuallyHidden } from 'react-aria';
import { type SliderState, useSliderState } from 'react-stately';
import { getSliderColors } from './helpers';
import styles from './slider.module.scss';
const Thumb = t0 => {
  const $ = _c(33);
  const {
    ariaLabelledBy,
    index,
    name,
    outputProps,
    state,
    trackRef,
    valueForLightTheme
  } = t0;
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== index || $[1] !== name || $[2] !== trackRef) {
    t1 = {
      index,
      inputRef,
      name,
      trackRef
    };
    $[0] = index;
    $[1] = name;
    $[2] = trackRef;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    inputProps,
    thumbProps
  } = useSliderThumb(t1, state);
  const sliderValue = state.values[0];
  const t2 = sliderValue / 10 * 100;
  let t3;
  if ($[4] !== t2) {
    t3 = getSliderColors(t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sliderStyles = t3;
  const {
    focusProps,
    isFocusVisible
  } = useFocusRing();
  const isLightTheme = valueForLightTheme === undefined ? sliderValue > 7 : sliderValue > valueForLightTheme;
  let t4;
  if ($[6] !== index || $[7] !== isFocusVisible || $[8] !== state) {
    t4 = classNames(styles.slider__thumb, {
      [styles["slider__thumb--is-dragging"]]: state.isThumbDragging(index),
      [styles["slider__thumb--is-focused"]]: isFocusVisible
    });
    $[6] = index;
    $[7] = isFocusVisible;
    $[8] = state;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== sliderStyles.thumbColor || $[11] !== thumbProps.style) {
    t5 = {
      ...thumbProps.style,
      background: sliderStyles.thumbColor
    };
    $[10] = sliderStyles.thumbColor;
    $[11] = thumbProps.style;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isLightTheme) {
    t6 = classNames(styles.slider__output, {
      [styles["slider__output--light"]]: isLightTheme
    });
    $[13] = isLightTheme;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== state) {
    t7 = state.getThumbValueLabel(0);
    $[15] = state;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== outputProps || $[18] !== t6 || $[19] !== t7) {
    t8 = <output {...outputProps} aria-hidden={true} className={t6}>{t7}</output>;
    $[17] = outputProps;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== focusProps || $[22] !== inputProps) {
    t9 = mergeProps(inputProps, focusProps);
    $[21] = focusProps;
    $[22] = inputProps;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== ariaLabelledBy || $[25] !== t9) {
    t10 = <VisuallyHidden><input aria-labelledby={ariaLabelledBy} ref={inputRef} {...t9} /></VisuallyHidden>;
    $[24] = ariaLabelledBy;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t4 || $[29] !== t5 || $[30] !== t8 || $[31] !== thumbProps) {
    t11 = <div {...thumbProps} className={t4} style={t5}>{t8}{t10}</div>;
    $[27] = t10;
    $[28] = t4;
    $[29] = t5;
    $[30] = t8;
    $[31] = thumbProps;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
export const Slider = t0 => {
  const $ = _c(37);
  const {
    ariaLabelledBy,
    defaultValue,
    name,
    onChangeHandler,
    valueForLightTheme: t1
  } = t0;
  const valueForLightTheme = t1 === undefined ? 7 : t1;
  const trackRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const numberFormatter = useNumberFormatter(t2);
  const t3 = defaultValue / 10 * 100;
  let t4;
  if ($[1] !== t3) {
    t4 = getSliderColors(t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [sliderStyles, setSliderStyles] = useState(t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setSliderStyles(getSliderColors(value / 10 * 100));
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleInputChange = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value_0 => handleInputChange(value_0);
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== onChangeHandler) {
    t7 = value_1 => {
      onChangeHandler(value_1);
    };
    $[5] = onChangeHandler;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== ariaLabelledBy || $[8] !== defaultValue || $[9] !== t7) {
    t8 = {
      "aria-labelledby": ariaLabelledBy,
      defaultValue,
      maxValue: 10,
      minValue: 0,
      onChange: t6,
      onChangeEnd: t7,
      step: 1
    };
    $[7] = ariaLabelledBy;
    $[8] = defaultValue;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const sliderProps = t8;
  let t9;
  if ($[11] !== numberFormatter || $[12] !== sliderProps) {
    t9 = {
      ...sliderProps,
      numberFormatter
    };
    $[11] = numberFormatter;
    $[12] = sliderProps;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const state = useSliderState(t9);
  let t10;
  if ($[14] !== sliderProps) {
    t10 = {
      ...sliderProps
    };
    $[14] = sliderProps;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const {
    groupProps,
    outputProps,
    trackProps
  } = useSlider(t10, state, trackRef);
  const sliderValue = state.values[0];
  const t11 = groupProps["aria-labelledby"];
  let t12;
  if ($[16] !== name || $[17] !== outputProps || $[18] !== state || $[19] !== t11 || $[20] !== valueForLightTheme) {
    t12 = <Thumb ariaLabelledBy={t11} index={0} name={name} outputProps={outputProps} state={state} trackRef={trackRef} valueForLightTheme={valueForLightTheme} />;
    $[16] = name;
    $[17] = outputProps;
    $[18] = state;
    $[19] = t11;
    $[20] = valueForLightTheme;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = styles[`slider__track-style--${sliderValue}`];
  let t14;
  if ($[22] !== sliderValue || $[23] !== t13) {
    t14 = classNames(styles["slider__track-style"], {
      [t13]: sliderValue
    });
    $[22] = sliderValue;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== sliderStyles.trackGradient) {
    t15 = {
      background: sliderStyles.trackGradient
    };
    $[25] = sliderStyles.trackGradient;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14 || $[28] !== t15) {
    t16 = <div className={t14} style={t15} />;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t12 || $[31] !== t16 || $[32] !== trackProps) {
    t17 = <div {...trackProps} ref={trackRef}>{t12}{t16}</div>;
    $[30] = t12;
    $[31] = t16;
    $[32] = trackProps;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== groupProps || $[35] !== t17) {
    t18 = <div className={styles["slider-container"]}><div {...groupProps} className={styles.slider}>{t17}</div></div>;
    $[34] = groupProps;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};